import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FIELD_KINDS, PANEL_TYPES, dimensionNames, metricNames, tableColumnsOf} from '../state/informeModel';
import {metricsLimitFor} from '../state/chartFields';
import {ADD_FIELD_OPTION} from '../state/addFieldFromPanel';
import {fieldLabel} from '../state/fieldLabels';
import {MetricsMultiSelect} from './MetricsMultiSelect';
import {Switch} from '../../buttons/Swtich';
import {FilterNotesInfoIcon} from '../../elements/FilterNotesDisplay';
import {groupsByFirstColumn, hidesEmptyRows, showsHeatmap} from './panelData';

/**
 * Las `options` del panel con una opción booleana de la tabla en `value`. El DEFAULT no se escribe, y
 * la FORMA de lo que había se conserva: un panel guardado vuelve del backend con `options: {}` y, al
 * volver al default, tiene que quedar en `{}` (no en `undefined`), o el informe queda «Sin guardar» sin
 * ningún cambio a la vista (review C-tabla-02). Sin `options`, sigue sin `options`. El backend tampoco
 * guarda el default (`TYPED_PANEL_OPTIONS`, `reports/services/layout_schema.py`).
 */
function withTableOption(options, key, value, defaultValue) {
    const {[key]: _ignored, ...rest} = options || {};
    if (value !== defaultValue) return {...rest, [key]: value};
    return options ? rest : undefined;
}

/** «Ocultar filas sin datos»: apagado es el default, se guarda el `true`. */
export function withHideEmptyRows(options, hide) {
    return withTableOption(options, 'hide_empty_rows', !!hide, false);
}

/** El mapa de calor: prendido es el default, se guarda el `false` (tanda iVirtual, card xjcyp0F5). */
export function withHeatmap(options, enabled) {
    return withTableOption(options, 'heatmap', !!enabled, true);
}

/** «Agrupar por la primera columna»: prendido es el default, se guarda el `false` (card mvljGqm8). */
export function withGroupByFirstColumn(options, enabled) {
    return withTableOption(options, 'group_by_first_column', !!enabled, true);
}

/**
 * Un interruptor de la tabla, con su rótulo y su ⓘ. El rótulo también prende y apaga, como el texto
 * de un checkbox (review C-tabla-09). No es un `<label>` porque el `Switch` no deja ponerle `id` a su
 * input; el teclado entra por el propio interruptor, que ya tiene el foco y el nombre accesible.
 */
function TableOptionToggle({panel, name, testId, label, tips, checked, onToggle}) {
    const labelId = `inf-${name}-${panel.uid || panel.id}`;
    return (
        <span className="inf-table-toggle" data-testid={testId}>
            <Switch name={labelId} checked={checked} aria-labelledby={labelId} messageToAlertUserIfChecked={null}
                    style={{margin: 0}}
                    onChange={(e) => onToggle(e.target.checked)}/>
            <span id={labelId} className="inf-table-toggle-label" onClick={() => onToggle(!checked)}>{label}</span>
            <FilterNotesInfoIcon texts={tips} position="below-left" testId={`${testId}-info`}/>
        </span>
    );
}

/**
 * Los interruptores de la tabla, que se guardan con el informe (y por eso el cliente ve en el portal lo
 * que dejó la agencia, sin poder tocarlo):
 *  - «Ocultar filas sin datos» (Hesta, E7). Si la tabla vino recortada (`truncated`: el motor la cortó
 *    en su límite de filas), su ⓘ avisa que pueden verse menos filas que el límite.
 *  - «Mapa de calor». Hasta el 2026-10-21 era una preferencia del NAVEGADOR heredada del Explorador:
 *    apagarlo no se guardaba, valía para todas las tablas, y el cliente lo veía prendido y con su switch.
 *  - «Agrupar por la primera columna», sólo con 2+ dimensiones: con una sola no hay nada que agrupar.
 */
function TableOptionsToggles({panel, onChange, truncated, dimensionCount}) {
    const {t} = useTranslation();
    const set = (withOption) => (value) => onChange({options: withOption(panel.options, value)});
    const hideTips = [t('informes.table.hide_empty_rows_tip', 'Oculta las filas que no tienen datos en ninguna métrica, ni en el período ni en la comparación. El total no cambia.')];
    if (truncated || panel.limit) hideTips.push(t('informes.table.hide_empty_rows_top_n', 'Con un límite de filas, pueden verse menos filas que el límite.'));
    return (
        <>
            <TableOptionToggle panel={panel} name="hide-empty" testId="hide-empty-rows"
                               label={t('informes.table.hide_empty_rows', 'Ocultar filas sin datos')} tips={hideTips}
                               checked={hidesEmptyRows(panel)} onToggle={set(withHideEmptyRows)}/>
            <TableOptionToggle panel={panel} name="heatmap" testId="table-heatmap"
                               label={t('informes.table.heatmap', 'Mapa de calor')}
                               tips={[t('informes.table.heatmap_tip', 'Pinta las celdas de las métricas según su valor.')]}
                               checked={showsHeatmap(panel)} onToggle={set(withHeatmap)}/>
            {dimensionCount >= 2 && (
                <TableOptionToggle panel={panel} name="group-first" testId="table-group-first-column"
                                   label={t('informes.table.group_by_first_column', 'Agrupar por la primera columna')}
                                   tips={[t('informes.table.group_by_first_column_tip', 'Muestra una sola vez el valor de la primera columna cuando se repite en las filas siguientes. Se aplica cuando la tabla está ordenada por esa columna.')]}
                                   checked={groupsByFirstColumn(panel)} onToggle={set(withGroupByFirstColumn)}/>
            )}
        </>
    );
}

/**
 * Un `<select>` de un valor con «+ Agregar otra…» al final (C-05). Con el puntero, elegir esa opción
 * abre el modal en el acto. Con el teclado NO: en Windows cada flecha sobre un select cerrado dispara
 * `change`, y recorrer la lista abría el modal al pasar por la última (y otra vez después de cancelar).
 * Ahí la opción queda MARCADA y se abre con Enter o Espacio; salir del select la desmarca (review
 * C-rp-fe-aprende-05). El select se dimensiona por lo elegido (`-with-add`), no por la opción más larga
 * (C-rp-fe-aprende-06).
 */
function SelectWithAdd({value, ariaLabel, placeholder, options, labelOf, addLabel, onPick, onAdd}) {
    const [armed, setArmed] = useState(false);
    const byPointer = useRef(false);
    const add = () => {
        setArmed(false);
        onAdd();
    };
    const onChange = (e) => {
        if (e.target.value !== ADD_FIELD_OPTION) {
            setArmed(false);
            onPick(e.target.value);
        } else if (byPointer.current) {
            add();
        } else {
            setArmed(true);
        }
    };
    const onKeyDown = (e) => {
        byPointer.current = false;
        if (armed && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            add();
        }
    };
    const pointer = () => { byPointer.current = true; };
    return (
        <select className={`inf-panel-select${onAdd ? ' -with-add' : ''}`} value={armed ? ADD_FIELD_OPTION : value}
                aria-label={ariaLabel} onChange={onChange} onKeyDown={onKeyDown} onPointerDown={pointer}
                onMouseDown={pointer} onBlur={() => setArmed(false)}>
            {!value && <option value="">{placeholder}</option>}
            {options.map((id) => <option key={id} value={id}>{labelOf(id)}</option>)}
            {onAdd && <option value={ADD_FIELD_OPTION}>{addLabel}</option>}
        </select>
    );
}

/**
 * Los selectores de un panel (mockup): dimensión y métricas en barras y torta, métricas en la
 * evolución, "+ CAMBIAR MÉTRICA" en las destacadas, y en la tabla el recordatorio de que sus
 * columnas son las de la plantilla. Son CONTROLADOS: cada cambio va a la plantilla
 * (`onChange(patch)`), que es lo que hace pulsar el chip. Los widgets del Explorador con selects
 * propios no sirven acá porque avisan "algo cambió" sin decir qué.
 *
 * Las opciones muestran el NOMBRE del catálogo, no el id técnico (QA UI-06: el select decía
 * «Campana» y la tabla «Campaña», la misma dimensión con dos nombres en la misma pantalla).
 *
 * Cada tabla tiene SU recorte de columnas (card hwuk9PW7). Hasta el 2026-09-09 todas las tablas
 * de un informe mostraban las métricas y dimensiones de la hoja —eran clones—, y su «Editar»
 * abría el modal de la hoja: tocar una las cambiaba todas. Sigue habiendo una sola verdad por
 * pregunta: la hoja define QUÉ CAMPOS entran al informe (y es sobre eso que eligen los
 * gráficos), la tabla define CUÁLES MUESTRA y en qué orden.
 *
 * `onAddField(kind)`: cada selector de un gráfico termina en «+ Agregar otra métrica» (o dimensión),
 * que abre «Métricas y dimensiones» y vuelve con el campo nuevo elegido (C-05, `addFieldFromPanel.js`).
 * Sin la prop (el portal, una vista de sólo lectura) no se ofrece.
 */
export function PanelControls({panel, fields, fieldsData, onChange, onEditFields, onAddField = null, readOnly = false,
                                  tableTruncated = false}) {
    const {t} = useTranslation();
    if (readOnly) return null;
    const dimensions = dimensionNames(fields);
    const metrics = metricNames(fields);
    const selectedMetrics = panel.metrics || [];
    const metric = selectedMetrics[0] || '';
    const nameOf = (id) => fieldLabel(id, fieldsData);
    const addMetricLabel = t('informes.panel.add_metric', '+ Agregar otra métrica');
    const addDimensionLabel = t('informes.panel.add_dimension', '+ Agregar otra dimensión');

    // La opción de agregar va al FINAL y no cambia el valor: el select es controlado, así que sin
    // `onChange` del valor React lo deja donde estaba mientras se abre el modal.
    const addOf = (kind) => (onAddField ? () => onAddField(kind) : null);

    // `ariaLabel`, no `label`: el parámetro se llamaba igual que el resolutor del catálogo y lo
    // tapaba, así que `label(m)` invocaba un string y toda la pantalla caía al ErrorBoundary.
    const metricSelect = (ariaLabel) => (
        <SelectWithAdd value={metric} ariaLabel={ariaLabel} placeholder={t('informes.panel.pick_metric', 'Métrica…')}
                       options={metrics} labelOf={nameOf} addLabel={addMetricLabel}
                       onPick={(value) => onChange({metrics: [value]})} onAdd={addOf(FIELD_KINDS.METRIC)}/>
    );
    const metricsSelect = (
        <MetricsMultiSelect value={selectedMetrics} options={metrics} limit={metricsLimitFor(panel.type)}
                            labelOf={nameOf} ariaLabel={t('informes.panel.metrics', 'Métricas')}
                            onChange={(next) => onChange({metrics: next})}
                            addLabel={addMetricLabel}
                            onAdd={onAddField ? () => onAddField(FIELD_KINDS.METRIC) : null}/>
    );
    const dimensionSelect = (
        <SelectWithAdd value={panel.dimension || ''} ariaLabel={t('informes.panel.dimension', 'Dimensión')}
                       placeholder={t('informes.panel.pick_dimension', 'Dimensión…')} options={dimensions}
                       labelOf={nameOf} addLabel={addDimensionLabel}
                       onPick={(value) => onChange({dimension: value})} onAdd={addOf(FIELD_KINDS.DIMENSION)}/>
    );

    switch (panel.type) {
        case PANEL_TYPES.KPI:
            return <div className="inf-panel-controls-inner">{metricSelect(t('informes.panel.change_metric', 'Cambiar métrica'))}</div>;
        case PANEL_TYPES.PIE:
            // Exactamente una métrica: lo exige el validador del backend («Una torta lleva
            // exactamente una métrica»), así que acá no hay multi-select que ofrecer.
            return <div className="inf-panel-controls-inner">{dimensionSelect}{metricSelect(t('informes.panel.metric', 'Métrica'))}</div>;
        case PANEL_TYPES.BARS:
            return <div className="inf-panel-controls-inner">{dimensionSelect}{metricsSelect}</div>;
        case PANEL_TYPES.LINE:
            return <div className="inf-panel-controls-inner">{metricsSelect}</div>;
        case PANEL_TYPES.TABLE: {
            // El conteo es el de ESTA tabla, no el de la hoja: es la única señal en pantalla de
            // que dos tablas del mismo informe pueden mostrar cosas distintas.
            const own = tableColumnsOf(panel, fields);
            const columns = own.dimensions.length + own.metrics.length;
            return (
                <div className="inf-panel-controls-inner inf-table-columns">
                    <span className="inf-table-columns-note">
                        {t('informes.panel.table_columns', {n: columns,
                            defaultValue: 'Columnas de esta tabla: {{n}}'})}
                    </span>
                    <button type="button" className="inf-table-columns-edit" onClick={onEditFields}
                            data-testid="edit-table-columns">
                        {t('informes.panel.table_columns_edit', 'Editar')}
                    </button>
                    <TableOptionsToggles panel={panel} onChange={onChange} truncated={tableTruncated}
                                         dimensionCount={own.dimensions.length}/>
                </div>
            );
        }
        default:
            return null;
    }
}
